import type { FaqItem } from '@/data/faq';
import { faqs } from '@/data/faq';
import {
  SITE_DEFAULT_DESCRIPTION,
  SITE_DEFAULT_TITLE,
  SITE_NAME,
  SITE_PHONE_SCHEMA,
  SITE_URL,
  SITE_DEFAULT_OG_IMAGE,
} from './siteConfig';

export type HomeSeoEntry = {
  /** Sayfa açılınca kaydırılacak bölüm; null = en üst */
  scrollSectionId: string | null;
  title: string;
  description: string;
  /** JSON-LD Breadcrumb ikinci kırılım; ana sayfada yok */
  breadcrumbName?: string;
};

export const HOME_ROUTE_SEO: Record<string, HomeSeoEntry> = {
  '/': {
    scrollSectionId: null,
    title: SITE_DEFAULT_TITLE,
    description: SITE_DEFAULT_DESCRIPTION,
  },
  '/hizmetler': {
    scrollSectionId: 'services',
    title: 'Kombi Servisi ve Hizmetler | Kombi Kurumsal Servis',
    description:
      'Kombi tamiri, bakımı, montajı ve petek temizliği. Tüm markalarda yerinde servis, şeffaf fiyat ve garantili yedek parça. Randevu ve bilgi: 0533 283 66 79.',
    breadcrumbName: 'Hizmetler',
  },
  '/kombi-tamiri': {
    scrollSectionId: 'services',
    title: 'Kombi Tamiri | Yerinde Arıza ve Onarım — Kombi Kurumsal Servis',
    description:
      'Kombi arızalarında hızlı teşhis ve yerinde tamir. Anakart, fan, eşanjör, gaz grubu ve tüm parça değişimleri. Aynı gün servis imkânı. Arayın: 0533 283 66 79.',
    breadcrumbName: 'Kombi tamiri',
  },
  '/kombi-bakimi': {
    scrollSectionId: 'maintenance',
    title: 'Kombi Bakımı | Yıllık Kontrol ve Verimlilik — Kombi Kurumsal Servis',
    description:
      'Kış öncesi kombi bakımı, güvenlik ve verimlilik kontrolleri, temizlik ve ayar. Periyodik bakımla arıza riskini azaltın. Randevu: 0533 283 66 79.',
    breadcrumbName: 'Kombi bakımı',
  },
  '/kombi-montaji': {
    scrollSectionId: 'services',
    title: 'Kombi Montajı | Güvenli Kurulum — Kombi Kurumsal Servis',
    description:
      'Yeni kombi montajı ve değişiminde profesyonel kurulum, test ve devreye alma. Tüm markalarda deneyimli usta ekibi. Bilgi: 0533 283 66 79.',
    breadcrumbName: 'Kombi montajı',
  },
  '/acil-kombi-servisi': {
    scrollSectionId: 'emergency',
    title: 'Acil Kombi Servisi | 7/24 Hızlı Müdahale — Kombi Kurumsal Servis',
    description:
      'Gece-gündüz acil kombi servisi. Arıza kaydı sonrası hızlı ekip yönlendirmesi, yerinde müdahale. Acil hat: 0533 283 66 79.',
    breadcrumbName: 'Acil kombi servisi',
  },
  '/kombi-markalari': {
    scrollSectionId: 'brands',
    title: 'Servis Verilen Kombi Markaları | Kombi Kurumsal Servis',
    description:
      'ECA, Demirdöküm, Airfel, Bosch, Vaillant, Ariston, Baymak ve daha fazlası. Tüm markalarda yedek parça ve teknik servis. Arayın: 0533 283 66 79.',
    breadcrumbName: 'Markalar',
  },
  '/neden-biz': {
    scrollSectionId: 'whyus',
    title: 'Neden Kombi Kurumsal Servis? | Deneyim ve Garanti',
    description:
      'Deneyimli usta kadrosu, şeffaf fiyat, orijinal yedek parça ve memnuniyet odaklı hizmet. Kombi tamiri ve bakımında güvenilir adres: 0533 283 66 79.',
    breadcrumbName: 'Neden biz',
  },
  '/sss': {
    scrollSectionId: 'faq',
    title: 'Sık Sorulan Sorular | Kombi Tamiri ve Bakım — Kombi Kurumsal Servis',
    description:
      'Kombi bakım sıklığı, tamir süresi, anakart, garanti ve ücretlendirme hakkında sık sorulan soruların yanıtları. 0533 283 66 79.',
    breadcrumbName: 'SSS',
  },
  '/iletisim': {
    scrollSectionId: 'contact',
    title: 'İletişim ve Servis Talebi | Kombi Kurumsal Servis',
    description:
      'Kombi servisi randevusu ve arıza bildirimi için telefon ve WhatsApp. Uzman ekibimize hemen ulaşın: 0533 283 66 79.',
    breadcrumbName: 'İletişim',
  },
  '/kombi-cesitleri': {
    scrollSectionId: 'boiler-types',
    title: 'Kombi Çeşitleri | Yoğuşmalı ve Hermetik — Kombi Kurumsal Servis',
    description:
      'Yoğuşmalı, tam yoğuşmalı ve hermetik kombi tiplerinde servis ve bakım. Cihazınıza uygun çözüm için uzman destek: 0533 283 66 79.',
    breadcrumbName: 'Kombi çeşitleri',
  },
};

export const HOME_SEO_PATHS = Object.keys(HOME_ROUTE_SEO) as (keyof typeof HOME_ROUTE_SEO)[];

export function getHomeSeo(pathname: string): HomeSeoEntry {
  const key =
    pathname === '' || pathname === '/'
      ? '/'
      : pathname.replace(/\/+$/, '') || '/';
  return HOME_ROUTE_SEO[key] ?? HOME_ROUTE_SEO['/'];
}

export function buildFaqPageSchema(items: FaqItem[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

export function buildHomeJsonLd(pathname: string, seo: HomeSeoEntry) {
  const normalized =
    pathname === '' || pathname === '/'
      ? '/'
      : pathname.replace(/\/+$/, '') || '/';
  const canonical = normalized === '/' ? `${SITE_URL}/` : `${SITE_URL}${normalized}`;
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      inLanguage: 'tr-TR',
      publisher: { '@id': `${SITE_URL}/#business` },
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#business`,
      additionalType: 'https://schema.org/HVACBusiness',
      name: SITE_NAME,
      url: SITE_URL,
      image: [SITE_DEFAULT_OG_IMAGE],
      telephone: SITE_PHONE_SCHEMA,
      description: seo.description,
      areaServed: { '@type': 'Country', name: 'Türkiye' },
      priceRange: '$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '00:00',
          closes: '23:59',
        },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Kombi teknik servis hizmetleri',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Kombi tamiri', serviceType: 'Onarım' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Kombi bakımı', serviceType: 'Bakım' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Kombi montajı', serviceType: 'Montaj' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Acil kombi servisi', serviceType: '7/24 destek' },
          },
        ],
      },
    },
    buildFaqPageSchema(faqs),
  ];

  if (normalized !== '/' && seo.breadcrumbName) {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Anasayfa',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: seo.breadcrumbName,
          item: canonical,
        },
      ],
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
